import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSanityContent } from '../hooks/useSanityContent';
import { fetchServicesCutIn } from '../lib/sanity';
import { defaultServicesCutIn } from '../lib/defaultContent';
import type { ServicesCutInContent } from '../lib/types';

const ServicesCutIn: React.FC = () => {
  const { content } = useSanityContent<ServicesCutInContent>(
    fetchServicesCutIn,
    defaultServicesCutIn
  );
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current) {
      // Ensure video plays on mount
      videoRef.current.play().catch(err => {
        console.log('Video autoplay prevented:', err);
      });
    }
  }, [content.videoUrl]);

  return (
    <section className="bg-primary-black overflow-hidden">
      <div className="grid lg:grid-cols-2">

        {/* ── Left: video cut-in — flush, no padding, no radius ── */}
        <div className="relative h-72 lg:h-auto overflow-hidden bg-black">
          <video
            ref={videoRef}
            key={content.videoUrl}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            onLoadedData={(e) => {
              const video = e.currentTarget;
              video.play().catch(err => console.log('Video play error:', err));
            }}
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source src={content.videoUrl} type="video/mp4" />
          </video>
          {/* Right-side scrim so it blends into the dark panel */}
          <div className="absolute inset-0 bg-gradient-to-l from-primary-black/60 via-transparent to-transparent" />
        </div>

        {/* ── Right: content ── */}
        <motion.div
          initial={{ opacity: 0, x: 24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.6 }}
          className="flex flex-col justify-center px-6 sm:px-10 lg:px-16 xl:px-20 py-14 lg:py-20"
        >
          <span className="text-primary-red text-xs font-inter font-bold uppercase tracking-[0.2em] mb-5">
            {content.label}
          </span>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-playfair font-bold text-white leading-tight mb-10">
            {content.title}
          </h2>

          {/* Steps */}
          <div className="space-y-7">
            {content.steps.map((step, i) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="flex gap-5 items-start"
              >
                <span className="text-primary-red font-playfair font-bold text-2xl leading-none w-8 flex-shrink-0 mt-0.5">
                  {step.number}
                </span>
                <div className="border-t border-white/10 pt-3 flex-1">
                  <h4 className="text-white font-inter font-semibold text-sm sm:text-base mb-1">
                    {step.title}
                  </h4>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {step.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <Link
            to={content.ctaLink}
            className="mt-10 inline-flex items-center gap-2 text-white font-inter font-semibold text-sm group self-start"
          >
            {content.ctaText}
            <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </motion.div>


      </div>
    </section>
  );
};

export default ServicesCutIn;
